import dayjs, { type Dayjs } from 'dayjs'
import React from 'react'

import useRebookEvent from '@/legacy-src/components/reservation/hooks/useRebookEvent'
import { type ServiceType } from '@/legacy-src/types/GeneralStateType'
import { type TherapistType } from '@/legacy-src/types/TherapistStateType'

import { ClientBookingFreeIntroSessionBlocker } from '@/src/domains/client-booking/components/ClientBookingFreeIntroSessionBlocker'
import { FreeSlotTypeFilter } from '@/src/domains/client-booking/constants'
import { useClientBookingFreeSlots } from '@/src/domains/client-booking/hooks/useClientBookingFreeSlots'
import type { FreeSlotTypeFilterWithAvailability } from '@/src/domains/client-booking/types'

import { ClientBookingAvailableSlotsUI } from './ClientBookingAvailableSlots.ui'
import { ClientBookingAvailableSlotsEmpty } from './ClientBookingAvailableSlotsEmpty'
import { useClientFreeIntroSessionEligibility } from './hooks/useClientFreeIntroSessionEligibility'

interface ClientBookingAvailableSlotsProps {
  day: Dayjs
  therapist: TherapistType
  service: ServiceType
  type: FreeSlotTypeFilterWithAvailability
}

export const ClientBookingAvailableSlots = ({ day, therapist, service, type }: ClientBookingAvailableSlotsProps) => {
  const { isRebookingInProgress } = useRebookEvent()
  const { eligible, isLoading: isEligibilityLoading } = useClientFreeIntroSessionEligibility(service.type)

  const endOfMonth = day ? day.endOf('month').endOf('day').unix() : 0

  const { slots } = useClientBookingFreeSlots({
    therapistId: therapist.therapistId,
    serviceId: service.id,
    dateTo: endOfMonth,
    type,
  })

  if (!isEligibilityLoading && !eligible) {
    return <ClientBookingFreeIntroSessionBlocker />
  }

  const slotsForDate = slots?.filter((slot) => dayjs(slot.startDate).isSame(day, 'day'))
  const sortedSlots = slotsForDate?.sort((a, b) => (dayjs(a.startDate).isBefore(b.startDate) ? -1 : 1))

  if (sortedSlots) {
    if (sortedSlots.length === 0 && type === FreeSlotTypeFilter.OFFLINE && !isRebookingInProgress) {
      return <ClientBookingAvailableSlotsEmpty />
    }

    return <ClientBookingAvailableSlotsUI day={day} service={service} therapist={therapist} slots={sortedSlots} />
  }

  return null
}
